import { occupiedCells } from './shape';
/**
 * Module 63 — powered gates, as a pure function of where the blocks are.
 *
 * ## Why there is no state here
 *
 * Power is a **hold, not a latch** (module CLAUDE.md decision 5). A block
 * covering a plate opens its gate; moving that block off closes it again. The
 * hold IS the puzzle — one block parks as an anchor while another leaves, so
 * the player chooses which block is sacrificed and frees it last.
 *
 * That design choice has a direct consequence for this file: there is nothing
 * to store. No open/closed flag, no visit history, no per-move bookkeeping.
 * `poweredPlateIds` is a function of block positions and nothing else, which is
 * what lets the renderer recompute it mid-drag against a *prospective* position
 * (WP-63.C1) without any risk of the preview and the model disagreeing — and
 * what lets the solver ask the same question about a hypothetical node without
 * having to unwind anything.
 *
 * A latch would have been cheaper and was rejected: it collapses the mechanic to
 * "visit the plate first", one extra step rather than a standing constraint.
 */
/** `"row,col"`, the same key form the rest of the model uses. */
const key = (row, col) => `${row},${col}`;
/**
 * Which plates are covered right now.
 *
 * A plate is powered when ANY block's occupied cells include its cell. The
 * plate is a floor feature: blocks pass over it freely, it never blocks
 * movement, and it does not care WHICH block is standing on it — colour, shape
 * and axis are all irrelevant here. That last point is deliberate and is the
 * reason this takes `Block[]` rather than a single block.
 *
 * Returns a `Set` because every caller asks membership questions, and because a
 * board may legitimately have several plates covered at once.
 */
export function poweredPlateIds(blocks, plates) {
    const powered = new Set();
    if (!plates || plates.length === 0)
        return powered;
    const covered = new Set();
    for (const block of blocks)
        for (const cell of occupiedCells(block))
            covered.add(key(cell.row, cell.col));
    for (const plate of plates)
        if (covered.has(key(plate.cell.row, plate.cell.col)))
            powered.add(plate.id);
    return powered;
}
/**
 * Is this gate open?
 *
 * An unpowered gate — one with no `powered` field — is always open, which is
 * every gate on every level before 25 and the reason a board with no plates
 * behaves exactly as it did before this module.
 *
 * `surge` lifts the power clause while Surge is open (the Surge power-up). It is kept
 * strictly separate from Rainbow's colour lift: Rainbow removes the colour
 * clause, Surge removes this one, and neither touches the other (decision 6).
 * Letting one imply the other would silently double Rainbow's value.
 *
 * A gate whose `plateId` matches no plate on the board reads as CLOSED rather
 * than open. That is the safe direction for a dangling reference: a gate that
 * refuses is a visibly stuck level, while a gate that silently opens is a level
 * whose difficulty quietly evaporated and which the solver would still call
 * solvable.
 */
export function gateIsOpen(exit, poweredIds, surge = false) {
    if (!exit.powered)
        return true;
    if (surge)
        return true;
    return poweredIds.has(exit.powered.plateId);
}
